// components/skeletons/SheetsListSkeleton.tsx
// Placeholder for the Sync History table on the Sheets (sheet imports) page
// while the first page of sheets loads: a header row plus sheet rows with
// expander, sheet title + link, last pull, leads, spend and actions.
// Drop it in where the table goes; the rest of the page stays on screen.

import React from 'react';
import { G } from '../GlobalComponents/uiTokens';
import { Bone, SkeletonStyles } from './Bone';

interface SheetsListSkeletonProps {
  /** show the Spend column (only users who can see spend have it) */
  showSpend?: boolean;
  rows?: number;
}

const SheetsListSkeleton: React.FC<SheetsListSkeletonProps> = ({ showSpend = false, rows = 5 }) => {
  // expander · sheet · last pull · leads · [spend] · actions
  const cols: { w: string; grow?: boolean; head: number; cell: React.ReactNode }[] = [
    { w: '2.6rem', head: 0, cell: <Bone w={12} h={12} r={3} /> },
    {
      w: '210px',
      grow: true,
      head: 40,
      cell: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <Bone w={30} h={30} r={8} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
            <Bone w="62%" h={10} />
            <Bone w="85%" h={8} />
          </div>
        </div>
      ),
    },
    {
      w: '140px',
      head: 55,
      cell: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Bone w="70%" h={10} />
          <Bone w={56} h={16} r={20} />
        </div>
      ),
    },
    { w: '90px', head: 40, cell: <Bone w={34} h={12} /> },
    ...(showSpend ? [{ w: '100px', head: 40, cell: <Bone w={60} h={12} /> }] : []),
    {
      w: '150px',
      head: 50,
      cell: (
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
          <Bone w={26} h={26} r={7} />
          <Bone w={26} h={26} r={7} />
          <Bone w={26} h={26} r={7} />
        </div>
      ),
    },
  ];

  return (
    <div role="status" aria-busy="true" aria-label="Loading sheets" style={{ minWidth: '640px' }}>
      <SkeletonStyles />
      <div style={{ display: 'flex', alignItems: 'center', background: '#fafbfb', borderBottom: `1px solid ${G.soft}` }}>
        {cols.map((c, i) => (
          <div key={i} style={{ width: c.w, flex: c.grow ? 1 : undefined, flexShrink: 0, padding: '12px 13px', display: 'flex', justifyContent: i === cols.length - 1 ? 'center' : 'flex-start' }}>
            {c.head > 0 && <Bone w={c.head} h={8} />}
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{ display: 'flex', alignItems: 'center', borderBottom: r === rows - 1 ? 'none' : `1px solid ${G.softer}` }}>
          {cols.map((c, i) => (
            <div key={i} style={{ width: c.w, flex: c.grow ? 1 : undefined, flexShrink: 0, padding: '12px 13px' }}>
              {c.cell}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default SheetsListSkeleton;